import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  any,
  arrayOf,
  bool,
  func,
  number,
  object,
  oneOf,
  oneOfType,
  string
} from "prop-types";

import componentSizes from "../../../../constants/componentSizes";
import isInViewport from "../../../../utilities/isInViewport";
import {
  filterOptions,
  initialState,
  optionId
} from "../../../utilities/comboboxUtilities";
import useComboboxHandlers from "../../../utilities/useComboboxHandlers/useComboboxHandlers";
import useSanitizedInput from "../../../utilities/useSanitizedInput";
import * as S from "./styled";

const autocompleteOptions = {
  list: "list",
  none: "none"
};

const Combobox = React.forwardRef(
  (
    {
      autocomplete,
      className,
      clearSearchOnSelect,
      defaultValue,
      id,
      isValid,
      labelIdentity,
      onChange,
      onKeyDown,
      onKeyUp,
      onOptionSelect,
      options,
      size,
      value,
      ...props
    },
    ref
  ) => {
    const listboxId = `${id}-listbox`;
    const [search, setSearch] = useState(
      initialState({ defaultValue, labelIdentity, value })
    );
    const [isVisible, setVisible] = useState(false);
    const keyUpHandler = useSanitizedInput(onKeyUp);

    const datalistRef = useRef(null);
    const optionsRefs = useRef([]);
    optionsRefs.current = [];

    const hideDatalistHandler = () => {
      setVisible(false);
      setSearch("");
    };

    const focusHandler = e => {
      setVisible(true);
      props.onFocus(e);
    };

    const clickHandler = e => {
      setVisible(true);
      props.onFocus(e);
    };

    const blurHandler = e => {
      setVisible(false);
      props.onBlur(e);
    };

    const changeHandler = e => {
      const inputValue = e.target.value;
      const option = options.find(o => labelIdentity(o) === inputValue) || null;

      if (value === undefined) {
        setSearch(inputValue);
      }

      setVisible(true);

      onChange(e, inputValue, option);
    };

    const selectHandler = (e, option) => {
      e.preventDefault();

      const newSearch = labelIdentity(option);

      if (clearSearchOnSelect) {
        setSearch("");
      } else {
        setSearch(newSearch);
      }

      setVisible(false);
      onChange(e, newSearch, option);
      onOptionSelect(option);
    };

    const filteredOptions = useMemo(
      () =>
        autocomplete === autocompleteOptions.none
          ? options
          : filterOptions({ options, identity: labelIdentity, search }),
      [search, options, autocomplete]
    );

    const isExpanded = useMemo(() => isVisible && filteredOptions.length > 0, [
      isVisible,
      filteredOptions
    ]);

    const {
      activeIndex,
      activeItem,
      callback: comboboxHandler,
      previousActiveIndex
    } = useComboboxHandlers({
      onHide: hideDatalistHandler,
      onSelect: selectHandler,
      options: filteredOptions,
      isExpanded
    });

    useEffect(() => {
      if (activeIndex > -1) {
        const activeElement = optionsRefs?.current[activeIndex];

        if (
          isExpanded &&
          activeElement &&
          !isInViewport(activeElement, datalistRef.current)
        ) {
          optionsRefs.current[activeIndex].scrollIntoView({
            block: previousActiveIndex > activeIndex ? "start" : "end",
            inline: "nearest",
            behavior: "auto"
          });
        }
      }
    }, [activeIndex, isExpanded]);

    useEffect(() => {
      if (value !== undefined) {
        setSearch(labelIdentity(value));
      }
    }, [value]);

    const keyDownHandler = e => {
      comboboxHandler(e);
      onKeyDown(e);
    };

    return (
      <S.ComboboxWrapper
        aria-expanded={isExpanded}
        aria-owns={listboxId}
        className={className}
      >
        <S.Input
          {...props}
          ref={ref}
          aria-activedescendant={
            activeIndex !== -1 ? optionId(id, activeIndex) : null
          }
          aria-autocomplete={autocomplete}
          id={id}
          isValid={isValid}
          onBlur={blurHandler}
          onChange={changeHandler}
          onClick={clickHandler}
          onFocus={focusHandler}
          onKeyDown={keyDownHandler}
          onKeyUp={keyUpHandler}
          size={size}
          value={search}
        />
        <S.Datalist
          ref={datalistRef}
          aria-labelledby={props["aria-labelledby"]}
          id={listboxId}
          isExpanded={isExpanded}
        >
          {isExpanded &&
            filteredOptions.map((option, i) => (
              <S.Option
                key={labelIdentity(option)}
                ref={el => {
                  if (el) {
                    optionsRefs.current.push(el);
                  }
                }}
                id={optionId(id, i)}
                isActive={activeItem === option}
                onMouseDown={e => {
                  selectHandler(e, option);
                }}
              >
                {labelIdentity(option)}
              </S.Option>
            ))}
        </S.Datalist>
      </S.ComboboxWrapper>
    );
  }
);

Combobox.displayName = "Combobox";

Combobox.propTypes = {
  "aria-labelledby": string,
  autocomplete: oneOf(Object.values(autocompleteOptions)),
  className: string,
  clearSearchOnSelect: bool,
  // eslint-disable-next-line react/require-default-props
  defaultValue: oneOfType([string, object, number]),
  id: string.isRequired,
  isValid: bool,
  labelIdentity: func,
  onBlur: func,
  onChange(props, propName) {
    const prop = props[propName];
    const propType = typeof prop;
    const stringifiedPropDefault = Combobox.defaultProps.onChange.toString();
    const stringifiedProp = prop.toString();

    if (propType !== "function") {
      return new Error("onChange must be a function");
    } else if (
      stringifiedProp === stringifiedPropDefault &&
      props.value !== undefined
    ) {
      return new Error(
        "It looks like you're using a controlled component without" +
          " an onChange handler. Consider adding an onChange handler to keep track of value."
      );
    }

    return null;
  },
  onClick: func,
  onFocus: func,
  onKeyDown: func,
  onKeyUp: func,
  onOptionSelect: func,
  options: arrayOf(any).isRequired,
  size: oneOf([componentSizes.SM, componentSizes.MD, componentSizes.LG]),
  // eslint-disable-next-line react/require-default-props
  value: oneOfType([string, object, number])
};

Combobox.defaultProps = {
  "aria-labelledby": null,
  autocomplete: autocompleteOptions.list,
  className: "",
  clearSearchOnSelect: false,
  isValid: true,
  labelIdentity: value => value,
  onBlur: () => {},
  onChange: () => {},
  onClick: () => {},
  onFocus: () => {},
  onKeyDown: () => {},
  onKeyUp: () => {},
  onOptionSelect: () => {},
  size: componentSizes.MD
};

export default Combobox;
